import { streamText, type CoreMessage, type LanguageModel, type Tool } from "ai"

export type RunEvent =
  | { type: "text"; delta: string }
  | { type: "tool-start"; id: string; name: string }
  | { type: "tool-call"; id: string; name: string; args: Record<string, any> }
  | { type: "tool-result"; id: string; name: string; result: unknown }
  | { type: "step"; index: number }

export interface RunOptions {
  model: LanguageModel
  // Rebuilt before every step: navigation changes the context and the open modules
  system: () => string
  tools: () => Record<string, Tool>
  messages: CoreMessage[]
  maxSteps: number
  signal?: AbortSignal
  onEvent: (e: RunEvent) => void
}

export interface RunResult {
  messages: CoreMessage[] // the new messages this run added
  steps: number
  hitStepLimit: boolean
}

// One model step per call, so each step gets the tools that exist at that moment
export async function runAssistant(opts: RunOptions): Promise<RunResult> {
  const added: CoreMessage[] = []
  let steps = 0

  for (; steps < opts.maxSteps; steps++) {
    if (opts.signal?.aborted) break
    opts.onEvent({ type: "step", index: steps })

    const result = streamText({
      model: opts.model,
      system: opts.system(),
      messages: [...opts.messages, ...added],
      tools: opts.tools(),
      maxSteps: 1,
      toolCallStreaming: true,
      abortSignal: opts.signal,
    })

    for await (const part of result.fullStream as AsyncIterable<any>) {
      switch (part.type) {
        case "text-delta":
          opts.onEvent({ type: "text", delta: part.textDelta })
          break
        case "tool-call-streaming-start":
          opts.onEvent({ type: "tool-start", id: part.toolCallId, name: part.toolName })
          break
        case "tool-call":
          opts.onEvent({ type: "tool-call", id: part.toolCallId, name: part.toolName, args: part.args ?? {} })
          break
        case "tool-result":
          opts.onEvent({ type: "tool-result", id: part.toolCallId, name: part.toolName, result: part.result })
          break
        case "error": {
          const e: any = part.error
          throw new Error(e?.message || e?.responseBody || (typeof e === "string" ? e : "The model request failed."))
        }
      }
    }

    const response = await result.response
    added.push(...(response.messages as CoreMessage[]))
    if ((await result.finishReason) !== "tool-calls") return { messages: added, steps: steps + 1, hitStepLimit: false }
  }

  return { messages: added, steps, hitStepLimit: steps >= opts.maxSteps }
}
